import React, { useState } from 'react';
import { DIETARY_OPTIONS, ALLERGEN_OPTIONS } from '../../constants';
import { 
  User, Camera, AlignLeft, Sparkles, 
  Settings2, Moon, Sun, Type, Eye, ChevronLeft, Save, Globe
} from 'lucide-react';

const SettingToggle = ({ label, description, icon: Icon, active, onClick }) => (
  <div className="flex items-center justify-between p-6 bg-stone-50 dark:bg-stone-900/50 rounded-[32px] border border-stone-100 dark:border-stone-800">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-2xl ${active ? 'bg-primary text-white' : 'bg-white dark:bg-stone-800 text-stone-400'} shadow-sm`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="font-black text-xs uppercase tracking-widest dark:text-white">{label}</p>
        <p className="text-[10px] text-stone-400 font-medium">{description}</p>
      </div>
    </div>
    <button 
      onClick={onClick}
      className={`w-14 h-8 rounded-full transition-all relative ${active ? 'bg-primary' : 'bg-stone-200 dark:bg-stone-700'}`}
    >
      <div className={`absolute top-1.5 w-5 h-5 bg-white rounded-full transition-all shadow-md ${active ? 'left-8' : 'left-1'}`} />
    </button>
  </div>
);

export const ProfileSettings = ({ 
  profile, settings, onSaveProfile, onUpdateSettings, onBack 
}) => {
  const [localProfile, setLocalProfile] = useState(profile);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    await onSaveProfile(localProfile);
    setIsSaving(false);
  };

  const toggleDiet = (option) => {
    const current = localProfile.dietary_preferences || [];
    const updated = current.includes(option) 
      ? current.filter(o => o !== option) 
      : [...current, option];
    setLocalProfile({ ...localProfile, dietary_preferences: updated });
  };

  const toggleAllergen = (option) => {
    const current = localProfile.allergens || [];
    const updated = current.includes(option) 
      ? current.filter(o => o !== option) 
      : [...current, option];
    setLocalProfile({ ...localProfile, allergens: updated });
  };

  return (
    <div data-testid="profile-settings" className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-12">
        <button onClick={onBack} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-400 hover:text-primary transition-colors">
          <ChevronLeft size={16}/> Volver al Perfil
        </button>
        <button 
          data-testid="save-profile-btn"
          onClick={handleSave}
          disabled={isSaving}
          className="bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-primary transition-all flex items-center gap-2"
        >
          {isSaving ? <span className="animate-pulse">Guardando...</span> : <><Save size={14}/> Guardar Perfil</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          <section className="bg-white dark:bg-stone-900 p-8 rounded-[48px] border border-stone-100 dark:border-stone-800 shadow-sm">
            <h3 className="text-xl font-black heading-serif mb-8 flex items-center gap-3 dark:text-white">
              <User size={20} className="text-primary"/> Mi Identidad
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-center gap-6 mb-8">
                <div className="relative group">
                  <img 
                    src={localProfile.avatar_url} 
                    className="w-24 h-24 rounded-3xl object-cover border-4 border-stone-50 shadow-lg"
                    alt="Preview"
                  />
                  <div className="absolute inset-0 bg-black/40 rounded-3xl opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer">
                    <Camera size={24} className="text-white"/>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-[9px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">URL del Avatar</label>
                  <input 
                    type="text"
                    value={localProfile.avatar_url}
                    onChange={(e) => setLocalProfile({...localProfile, avatar_url: e.target.value})}
                    className="w-full p-4 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none font-bold text-xs dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">Nombre para Mostrar</label>
                <input 
                  data-testid="profile-display-name"
                  type="text"
                  value={localProfile.display_name}
                  onChange={(e) => setLocalProfile({...localProfile, display_name: e.target.value})}
                  className="w-full p-5 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none font-bold dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">Biografía</label>
                <textarea 
                  data-testid="profile-bio"
                  value={localProfile.bio}
                  onChange={(e) => setLocalProfile({...localProfile, bio: e.target.value})}
                  className="w-full p-5 h-32 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none font-medium text-sm dark:text-white resize-none"
                />
              </div>
            </div>
          </section>

          <section className="bg-white dark:bg-stone-900 p-8 rounded-[48px] border border-stone-100 dark:border-stone-800 shadow-sm">
            <h3 className="text-xl font-black heading-serif mb-8 flex items-center gap-3 dark:text-white">
              <Sparkles size={20} className="text-primary"/> Preferencias
            </h3>

            <div className="mb-8">
              <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-4 ml-1">Dieta</p>
              <div className="flex flex-wrap gap-2">
                {DIETARY_OPTIONS.map(opt => (
                  <button 
                    key={opt}
                    onClick={() => toggleDiet(opt)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${localProfile.dietary_preferences?.includes(opt) ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-stone-50 dark:bg-stone-800 text-stone-400'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-4 ml-1">Alérgenos</p>
              <div className="flex flex-wrap gap-2">
                {ALLERGEN_OPTIONS.map(opt => (
                  <button 
                    key={opt}
                    onClick={() => toggleAllergen(opt)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${localProfile.allergens?.includes(opt) ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20' : 'bg-stone-50 dark:bg-stone-800 text-stone-400'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div className="lg:col-span-5 space-y-8">
          <section className="bg-white dark:bg-stone-900 p-8 rounded-[48px] border border-stone-100 dark:border-stone-800 shadow-sm">
            <h3 className="text-xl font-black heading-serif mb-8 flex items-center gap-3 dark:text-white">
              <Settings2 size={20} className="text-primary"/> App & Accesibilidad
            </h3>

            <div className="space-y-4">
              <SettingToggle 
                label="Modo Oscuro"
                description="Reduce la fatiga visual de noche"
                icon={settings?.isDarkMode ? Moon : Sun}
                active={settings?.isDarkMode}
                onClick={() => onUpdateSettings({ isDarkMode: !settings?.isDarkMode })}
              />
              <SettingToggle 
                label="Fuente Dislexia"
                description="Mejora la legibilidad del texto"
                icon={Type}
                active={settings?.dyslexicFont}
                onClick={() => onUpdateSettings({ dyslexicFont: !settings?.dyslexicFont })}
              />
              <SettingToggle 
                label="Texto Grande"
                description="Aumenta el tamaño de las fuentes"
                icon={AlignLeft}
                active={settings?.largeText}
                onClick={() => onUpdateSettings({ largeText: !settings?.largeText })}
              />
              <SettingToggle 
                label="Alto Contraste"
                description="Resalta elementos visuales"
                icon={Eye}
                active={settings?.highContrast}
                onClick={() => onUpdateSettings({ highContrast: !settings?.highContrast })}
              />
              <SettingToggle 
                label="Perfil Público"
                description="Otros chefs pueden ver tu perfil"
                icon={Globe}
                active={settings?.publicProfile}
                onClick={() => onUpdateSettings({ publicProfile: !settings?.publicProfile })}
              />
            </div>
          </section>

          <div className="p-8 bg-stone-900 rounded-[40px] text-white overflow-hidden relative group">
            <div className="absolute -right-10 -bottom-10 opacity-10 group-hover:scale-110 transition-transform">
              <Sparkles size={160} />
            </div>
            <h4 className="text-lg font-black heading-serif mb-2">Consejo Recetik</h4>
            <p className="text-stone-400 text-sm font-medium leading-relaxed">
              Mantener tus alérgenos actualizados nos ayuda a filtrar recetas que sean seguras para ti.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
